/**
 * App Store
 * Zustand store for current app/workspace state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { App, Workspace, Collection } from '@tpcms/core';

interface AppState {
    currentWorkspace: Workspace | null;
    currentApp: App | null;
    apps: App[];
    collections: Collection[];

    setCurrentWorkspace: (workspace: Workspace | null) => void;
    setCurrentApp: (app: App | null) => void;
    setApps: (apps: App[]) => void;
    setCollections: (collections: Collection[]) => void;

    fetchApps: () => Promise<void>;
    fetchCollections: (appId: string) => Promise<void>;
}

const API_URL = import.meta.env.VITE_API_URL || '';

export const useAppStore = create<AppState>()(
    persist(
        (set, get) => ({
            currentWorkspace: null,
            currentApp: null,
            apps: [],
            collections: [],

            setCurrentWorkspace: (workspace) => set({ currentWorkspace: workspace }),
            setCurrentApp: (app) => set({ currentApp: app }),
            setApps: (apps) => set({ apps }),
            setCollections: (collections) => set({ collections }),

            fetchApps: async () => {
                try {
                    const response = await fetch(`${API_URL}/api/v1/apps`);
                    if (response.ok) {
                        const { data } = await response.json();
                        set({ apps: data });
                        // Auto-select first app if none selected
                        if (!get().currentApp && data.length > 0) {
                            set({ currentApp: data[0] });
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch apps:', error);
                }
            },

            fetchCollections: async (appId: string) => {
                try {
                    const response = await fetch(`${API_URL}/api/v1/collections?appId=${appId}`);
                    if (response.ok) {
                        const { data } = await response.json();
                        set({ collections: data });
                    }
                } catch (error) {
                    console.error('Failed to fetch collections:', error);
                }
            },
        }),
        {
            name: 'tpcms-app',
            partialize: (state) => ({
                currentWorkspace: state.currentWorkspace,
                currentApp: state.currentApp,
            }),
        }
    )
);
